/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
	Box,
	Button,
	Center,
	Checkbox,
	Container,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	useBreakpointValue,
	useDisclosure,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
	useToast,
} from '@chakra-ui/react';
import { faFileExcel, faPlus, faPrint, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import { useEffect, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import PrintLayout from '../../../components/Print/PrintLayout';
import LoadingResponsive from '../../../components/Table/LoadingResponsive';
import datePickerFromToToDate from '../../../utils/datePickerFromToToDate';
import useStore from '../../../store';
import moment from 'moment';
import c from '../../../constant';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import PositionStatusAdd from './create';
import BreadcrumbComponent from '../../../components/BreadcrumbComponent/BreadcrumbComponent';
import getPositionLabel from '../../../utils/getPositionLabel';
import PositionStatusDetail from './detail';
import PositionStatusFilter from '../../../components/Filter/PositionStatusFilter';
import { ButtonDropdown } from '@cloudscape-design/components';
import PositionStatusExport from './export';
import SmallDownloadAndViewButton from '../../../components/Files/SmallDownloadAndViewButton';

const QUERY_DATA = gql`
	query get(
		$limit: Float
		$offset: Float
		$id: String
		$no: Float
		$rank: PositionStatusRankEnum
		$position: PositionStatusPositionEnum
		$office: String
		$department: String
		$dateOfLastPosition: DateTime
		$dateOfLastRank: DateTime
		$userId: String
	) {
		getPositionStatus(
			pagerInput: { limit: $limit, offset: $offset }
			no: $no
			id: $id
			rank: $rank
			position: $position
			dateOfLastRank: $dateOfLastRank
			dateOfLastPosition: $dateOfLastPosition
			office: $office
			department: $department
			userId: $userId
		) {
			positionStatus {
				id
				no
				userId
				rank
				position
				dateOfLastRank
				dateOfLastPosition
				office
				department
				attachmentFile
				createdBy
				createdAt
				notification {
					id
					read
				}
				user {
					id
					username
					fullName
					fullNameKhmer
					department
					position
					avatar
				}
			}
			totalPages
			totalRows
		}
	}
`;

const Q_DELETE_POSITION_STATUS = gql`
	mutation RemovePositionStatus($id: String!) {
		removePositionStatus(id: $id)
	}
`;

const MUTATION_READ_NOTIFICATION = gql`
	mutation read($id: String!) {
		readNotificationById(id: $id)
	}
`;

const PositionStatusRankLabel = {
	Level_7: 'អនុមន្រ្តីកម្រិត ១',
	Level_6: 'អនុមន្រ្តីកម្រិត ២',
	Level_5: 'អនុមន្រ្តីកម្រិត ៣',
	Level_4: 'នាយក្រមការ',
	Level_3: 'ក្រមការដើមខ្សែ',
	Level_2: 'ក្រមការ',
	Level_1: 'កម្មសិក្សា',
};

function PositionStatusList() {
	const location = useLocation();
	const history = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const toast = useToast();
	const currentPage = urlParams.get('page') ? urlParams.get('page') : 1;
	const no = urlParams.get('no') ? urlParams.get('no') : null;
	const id = urlParams.get('id') ? urlParams.get('id') : null;
	const position = urlParams.get('position') ? urlParams.get('position') : null;
	const rank = urlParams.get('rank') ? urlParams.get('rank') : null;
	const dateOfLastPosition = urlParams.get('dateOfLastPosition') ? urlParams.get('dateOfLastPosition') : null;
	const dateOfLastRank = urlParams.get('dateOfLastRank') ? urlParams.get('dateOfLastRank') : null;
	const office = urlParams.get('office') ? urlParams.get('office') : null;
	const department = urlParams.get('department') ? urlParams.get('department') : null;
	const userId = urlParams.get('userId') ? urlParams.get('userId') : null;
	const modalFilter = useDisclosure();
	const modalExport = useDisclosure();
	const detailModal = useDisclosure();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const { countUnreadNotifications } = useStore((state) => state.countUnreadNotifications);
	const setCountUnreadNotifications = useStore((state) => state.setCountUnreadNotifications);
	const [rowLimit, setRowLimit] = useState(10);
	const [positionStatusRecord, setPositionStatusRecord] = useState([]);
	const [exportRecords, setExportRecords] = useState([]);
	const [selectedPositionStatus, setSelectedPositionStatus] = useState(null);
	const [deletedId, setDeletedId] = useState(null);
	const [isOpeningDeleteDialog, setOpeningDeleteDialog] = useState(false);
	const deleteDialogRef = useRef();
	const deleteDialogCancelRef = useRef();
	const editModal = useDisclosure();
	const [readNotification, {}] = useMutation(MUTATION_READ_NOTIFICATION);
	const [loadData, { loading, data }] = useLazyQuery(QUERY_DATA, { fetchPolicy: 'network-only' });
	const [removePositionStatus, { loading: loadingRemovePositionStatus, data: dataRemovePositionStatus, error: errorRemovePositionStatus }] =
		useMutation(Q_DELETE_POSITION_STATUS);

	moment.locale(lang === 'kh' ? 'km' : 'en-gb');

	const showLargeScreen = useBreakpointValue({
		base: false,
		sm: false,
		md: true,
		lg: true, // >= 992px
		xl: true, // >= 1280px
	});

	const canSubmitPositionStatus = user?.role === 'admin' || (user?.position !== '1' && user?.role?.includes('employee'));

	function callbackFilter(e) {
		history.push(`/position-status${e}`);
	}

	const getVariables = () => ({
		offset: currentPage ? (currentPage - 1) * rowLimit : 0,
		limit: rowLimit,
		no: no ? parseFloat(no) : null,
		id: id || null,
		userId: userId || null,
		office: office || null,
		department: department || null,
		position: position || null,
		rank: rank || null,
		dateOfLastPosition: dateOfLastPosition ? datePickerFromToToDate(dateOfLastPosition) : null,
		dateOfLastRank: dateOfLastRank ? datePickerFromToToDate(dateOfLastRank) : null,
	});

	useEffect(() => {
		if (currentPage) {
			window.scrollTo(0, 0);
			loadData({
				variables: getVariables(),
			});
		}
	}, [rowLimit, currentPage, no, id, office, department, rank, position, dateOfLastPosition, dateOfLastRank, userId]);

	useEffect(() => {
		if (data) {
			setPositionStatusRecord(data?.getPositionStatus?.positionStatus);
		}
	}, [data]);

	async function openPositionStatus(item) {
		setSelectedPositionStatus(item);

		if (item?.notification?.id && item?.notification?.read === false) {
			await readNotification({
				variables: {
					id: item?.notification?.id,
				},
			});

			setCountUnreadNotifications(Math.max(countUnreadNotifications - 1, 0));
		}

		detailModal.onOpen();
	}

	function handlePageClick(data) {
		let selected = data.selected;
		const page = selected + 1;

		let url = `?page=${page}`;

		if (no) {
			url += `&no=${no}`;
		}

		if (office) {
			url += `&office=${encodeURIComponent(office)}`;
		}

		if (department) {
			url += `&department=${encodeURIComponent(department)}`;
		}

		if (rank) {
			url += `&rank=${encodeURIComponent(rank)}`;
		}

		if (position) {
			url += `&position=${encodeURIComponent(position)}`;
		}

		if (dateOfLastPosition) {
			url += `&dateOfLastPosition=${encodeURIComponent(dateOfLastPosition)}`;
		}

		if (dateOfLastRank) {
			url += `&dateOfLastRank=${encodeURIComponent(dateOfLastRank)}`;
		}

		if (userId) {
			url += `&userId=${encodeURIComponent(userId)}`;
		}

		history.push(`/position-status${url}`);
	}

	function actionMenu(row) {
		return (
			<Box onClick={(e) => e.stopPropagation()}>
				<Flex justifyContent="flex-end" alignItems="center">
					<ButtonDropdown
						className="action-button-table"
						variant="inline-icon"
						ariaLabel={t('Actions')}
						expandToViewport={true}
						items={[
							{ id: 'edit', text: t('Edit') },
							{ id: 'delete', text: <p style={{ color: 'red' }}>{t('Delete')}</p> },
						]}
						onItemClick={({ detail }) => {
							switch (detail?.id) {
								case 'edit':
									setSelectedPositionStatus(row);
									editModal?.onOpen();
									break;
								case 'delete':
									setDeletedId(row?.id);
									setOpeningDeleteDialog(true);
									break;
								default:
									break;
							}
						}}
					/>
				</Flex>
			</Box>
		);
	}

	function onRejectDeleteDialog() {
		setOpeningDeleteDialog(false);
		setDeletedId(null);
	}

	async function onApproveDeleteDialog() {
		if (!deletedId) return;

		await removePositionStatus({
			variables: { id: deletedId },
		});

		setOpeningDeleteDialog(false);
		setDeletedId(null);

		toast({
			title: t('Position status has been removed'),
			status: 'success',
			isClosable: true,
			position: 'top-right',
		});

		loadData({
			variables: getVariables(),
		});
	}

	return (
		<PrintLayout>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: 'Position Status',
							path: '#',
						},
					]}
				/>
				<Center className="show-only-printing">
					<Text textAlign="center" color="#62B3ED" fontFamily="Moul" fontSize={18}>
						ស្ថានភាពមុខងារបច្ចុប្បន្ន
					</Text>
				</Center>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex
							className="hidden-print responsive-header-for-table"
							bg="#FAFAFA"
							p="10px"
							pt="5px"
							pb="5px"
							mb="16px"
							alignItems="center"
							justifyContent="space-between"
							borderBottom="1px solid #dbdbdb"
						>
							<Flex justifyContent="flex-start">
								<Text alignItems="center" fontWeight="bold" fontSize="20px" pl={2}>
									{t('Position Status List')}
								</Text>
							</Flex>
							<Flex className="header-menu" alignItems="center" justifyContent="flex-end" minW="361px">
								<Button
									mr="2"
									onClick={() => window?.print()}
									leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
									colorScheme="gray"
									variant="solid"
									size="sm"
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
								>
									{t('Print')}
								</Button>

								<Button
									mr="2"
									onClick={() => {
										setExportRecords([]);
										modalExport?.onOpen();
									}}
									leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
									colorScheme="gray"
									variant="solid"
									size="sm"
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
								>
									{t('Export')}
								</Button>

								<Button
									onClick={modalFilter?.onOpen}
									leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
									colorScheme={`${no || department || office || dateOfLastPosition || dateOfLastRank || position || rank ? 'yellow' : 'gray'}`}
									variant="solid"
									size="sm"
									mr={user?.position !== '1' ? 2 : 0}
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7}"
								>
									{t('Filter')}
								</Button>

								{canSubmitPositionStatus && (
									<Button
										leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
										colorScheme="gray"
										variant="solid"
										size="sm"
										borderRadius={c.borderRadius}
										border="1px solid #bdc3c7"
										onClick={onOpen}
									>
										{t('Create')}
									</Button>
								)}
							</Flex>
						</Flex>

						<Box p="10px">
							<Table variant="simple" className="table-company-proposal-list responsive-table-container">
								<Thead>
									<Tr className="td-as-th">
										<Th align="left">{t('Number')}</Th>
										<Th align="left" style={{ width: '320px' }}>
											{t('Officials')}
										</Th>
										<Th align="left" style={{ width: '250px' }}>
											{t('Position')}
										</Th>
										<Th align="left" style={{ width: '330px' }}>
											{t('Date of Last Position')}
										</Th>
										<Th align="left" style={{ width: '400px' }}>
											{t('Rank')}
										</Th>
										<Th align="left" style={{ width: '390px' }}>
											{t('Date of Last Rank')}
										</Th>
										<Th align="right">{t('Document')}</Th>
										{canSubmitPositionStatus && <Th align="right">{t('Actions')}</Th>}
									</Tr>
								</Thead>
								<Tbody>
									{loading && <LoadingResponsive column={8} />}
									{data?.getPositionStatus?.positionStatus?.length === 0 && (
										<Tr>
											<Td colSpan={canSubmitPositionStatus ? 8 : 7} textAlign="left" py={6}>
												{t('No Data')}
											</Td>
										</Tr>
									)}
									{positionStatusRecord?.map((item, index) => {
										return (
											<Tr
												key={index}
												cursor="pointer"
												_hover={{ bg: 'gray.200' }}
												onClick={() => openPositionStatus(item)}
												style={{
													cursor: 'pointer',
													backgroundColor: item?.notification?.read === false ? '#EEEEEE' : null,
												}}
											>
												<Td>{item?.no ? String(item?.no).padStart(4, '0') : 'N/A'}</Td>
												<Td>{lang === 'en' ? item?.user?.fullName : item?.user?.fullNameKhmer}</Td>
												<Td>{lang === 'en' ? getPositionLabel(item?.position, 'en') : getPositionLabel(item?.position, 'kh')}</Td>
												<Td align="left">
													{item?.dateOfLastPosition ? moment(item?.dateOfLastPosition).format('DD MMM YYYY') : 'N/A'}
												</Td>
												<Td align="left">{PositionStatusRankLabel[item?.rank] || 'N/A'}</Td>
												<Td align="left" style={{ marginTop: showLargeScreen ? '0' : '10px' }}>
													{item?.dateOfLastRank ? moment(item?.dateOfLastRank).format('DD MMM YYYY') : 'N/A'}
												</Td>
												<Td align="right" style={{ width: '200px', marginTop: showLargeScreen ? '0' : '10px' }}>
													{item?.attachmentFile?.file?.length > 0 ? (
														<Flex
															className="viewContainer"
															ml={showLargeScreen ? '0' : '170px'}
															justifyContent="end"
															onClick={(e) => e.stopPropagation()}
														>
															<SmallDownloadAndViewButton
																w="100px"
																url={item?.attachmentFile?.file}
																size={item?.attachmentFile?.size}
																marginLeft={0}
															/>
														</Flex>
													) : (
														<Flex justifyContent="flex-end">
															<Text>N/A</Text>
														</Flex>
													)}
												</Td>
												{canSubmitPositionStatus && <Td align="right">{actionMenu(item)}</Td>}
											</Tr>
										);
									})}
								</Tbody>
								<tfoot className="responsive-footer">
									<Tr className="td-as-th">
										<Th align="left">{t('Number')}</Th>
										<Th align="left" style={{ width: '250px' }}>
											{t('Officials')}
										</Th>
										<Th align="left" style={{ width: '250px' }}>
											{t('Position')}
										</Th>
										<Th align="left" style={{ width: '250px' }}>
											{t('Date of Last Position')}
										</Th>
										<Th align="left" style={{ width: '300px' }}>
											{t('Rank')}
										</Th>
										<Th align="left" style={{ width: '300px' }}>
											{t('Date of Last Rank')}
										</Th>
										<Th align="right">{t('Document')}</Th>
										{canSubmitPositionStatus && <Th align="right">{t('Actions')}</Th>}
									</Tr>
								</tfoot>
							</Table>
							<Flex justifyContent="flex-start" className="show-only-printing">
								{t('Total Rows')}: {data?.getPositionStatus?.totalRows}
							</Flex>
							<Flex justifyContent="flex-end" className="hidden-print">
								{t('Total Rows')}: {data?.getPositionStatus?.totalRows}
							</Flex>
							{data?.getPositionStatus?.totalPages > 1 && (
								<Flex justifyContent="flex-end" w="100%" mt="4">
									<ReactPaginate
										previousLabel={<ChevronLeftIcon fontSize="20px" />}
										nextLabel={<ChevronRightIcon fontSize="20px" />}
										breakLabel={'...'}
										breakClassName={'break-me'}
										pageCount={data?.getPositionStatus?.totalPages}
										marginPagesDisplayed={2}
										pageRangeDisplayed={3}
										onPageChange={handlePageClick}
										containerClassName={'pagination'}
										activeClassName={'active'}
										initialPage={(currentPage - 1) | 0}
									/>
								</Flex>
							)}
							<Flex className="hidden-print" justifyContent="flex-end" mt="6">
								<Text mr="2">{t('Show more rows')}</Text>
								<Checkbox
									isChecked={rowLimit > 10}
									onChange={() => {
										handlePageClick({ selected: 0 });
										if (rowLimit > 10) {
											setRowLimit(10);
										} else {
											setRowLimit(300);
										}
									}}
								/>
							</Flex>
						</Box>
					</Box>
				</Center>
			</Container>

			<Modal size="xl" isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pt={2}>
					<ModalHeader>{t('Create a Position Status')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Flex justifyContent="center" pb={12}>
							<PositionStatusAdd
								onCloseModal={() => {
									onClose();
									loadData({ variables: getVariables() });
								}}
							/>
						</Flex>
					</ModalBody>
				</ModalContent>
			</Modal>

			<Modal size="2xl" isOpen={detailModal?.isOpen} onClose={detailModal?.onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pt={2}>
					<ModalHeader>{t('Detail')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Flex justifyContent="center" pb={12}>
							<PositionStatusDetail
								id={selectedPositionStatus?.id}
								onCloseModal={() => {
									detailModal?.onClose();
									loadData({ variables: getVariables() });
								}}
							/>
						</Flex>
					</ModalBody>
				</ModalContent>
			</Modal>

			<Modal size="xl" isOpen={editModal.isOpen} onClose={editModal.onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pt={2}>
					<ModalHeader>{t('Edit Position Status')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Flex justifyContent="center" pb={12}>
							<PositionStatusAdd
								id={selectedPositionStatus?.id}
								isEdit={true}
								onCloseModal={() => {
									editModal.onClose();
									loadData({ variables: getVariables() });
								}}
							/>
						</Flex>
					</ModalBody>
				</ModalContent>
			</Modal>

			<AlertDialog isOpen={isOpeningDeleteDialog} leastDestructiveRef={deleteDialogRef} onClose={onRejectDeleteDialog}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Delete Position Status')}
						</AlertDialogHeader>

						<AlertDialogBody>
							<Text fontSize="15px">{t("Are you sure? You can't undo this action afterwards")}</Text>
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button mr={4} isLoading={loadingRemovePositionStatus} ref={deleteDialogCancelRef} onClick={onRejectDeleteDialog}>
								{t('Cancel')}
							</Button>
							<Button isLoading={loadingRemovePositionStatus} colorScheme="red" onClick={onApproveDeleteDialog}>
								{t('Delete')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>

			<PositionStatusExport isOpen={modalExport?.isOpen} onClose={modalExport?.onClose} />

			<PositionStatusFilter
				callback={callbackFilter}
				isOpen={modalFilter?.isOpen}
				onClose={modalFilter?.onClose}
				office={office}
				department={department}
				no={no}
				position={position}
				rank={rank}
			/>
		</PrintLayout>
	);
}

export default PositionStatusList;
